import { Flex } from '@/styles/globalStyles';
import {
  Container,
  MapContainer,
  StyledChangeMunicipalityButton,
  StyledFab,
  StyledPaper,
  StyledScoreCardMapContainer,
  TitleHeader,
} from './styles';
import { Text } from '@/components/atoms/text';
import { useQuery, useApolloClient } from '@apollo/client';
import { MunicipalityWithDimensions } from '@/graphql/types/municipality';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  useMediaQuery,
} from '@mui/material';
import { Chat } from '@/components/organisms/chat';
import { QUERY_CONVERSATIONS_HISTORY_BY_ID } from '@/graphql/queries/chat';
import { ChatConversation, ChatStreamInput } from '@/graphql/types/chat';
import { DimensionsNote } from './dimensionsNote';
import { SparklesIcon } from '@/components/atoms/customIcons/sparklesIcon';
import { useTheme } from '@mui/material/styles';
import { StaticMap } from '../staticMap';
import type { GeometryItem } from '../staticMap/type';
import type { ViewportBounds } from '../staticMap/type';
import { usePolygonDatabase } from '@/hooks/usePolygonDatabase';
import { useTranslation } from '@/hooks/useTranslation';
import { Close, EditOutlined } from '@mui/icons-material';
import { Mobile } from '@/components/molecules/viewport/mobile';
import { SearchMunicipalityBottomSheet } from '../searchMunicipality/bottomsheet';
import { Desktop } from '@/components/molecules/viewport/desktop';
import { MunicipalityKpis } from './kpis';
import { SafeMarkdown } from '@/components/atoms/markdown/safe';
import type { Variable } from '@/graphql/types/variables';
import { QUERY_VARIABLES_BY_TYPE } from '@/graphql/queries/variables';
import { useLanguage } from '@/hooks/useLanguage';
import { MunicipalityAutocomplete } from '@/components/atoms/inputs/municipalityInput';
import { useChatStream } from '@/hooks/useChatStream';
import { MapCard } from '@/components/molecules/cards/map';
import { MapCardBackdrop } from '@/components/organisms/territorialDashboard/desktop/styles';
import { GeometryData } from '@/context/geometry';
import { QUERY_MUNICIPALITY_SCORES_DETAILED } from '@/graphql/queries/municipalities';
import type { MunicipalityScoresDetailedResponse } from '@/graphql/types/municipality';
import { Classification, ClassificationOpportunityAndRisk } from '@/types/classification';
import { useNavigate } from 'react-router-dom';
import { AppRouters } from '@/utils/constants/routers';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

interface MunicipalityProps {
  cdMun?: string;
  tabs: ReactNode;
  municipalityData?: MunicipalityWithDimensions;
  municipalityLoading: boolean;
  insightText?: string;
  onResetInsightText?: () => void;
  onMunicipalityChange?: (municipalityCode: string) => void;
}

export const Municipality = ({
  cdMun,
  tabs,
  municipalityData,
  municipalityLoading,
  insightText,
  onResetInsightText,
  onMunicipalityChange,
}: MunicipalityProps) => {
  const apolloClient = useApolloClient();
  const theme = useTheme();

  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [isChatOpen, setIsChatOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState<string | undefined>(undefined);
  const [streamInput, setStreamInput] = useState<ChatStreamInput | null>(null);
  const [variables, setVariables] = useState<Variable[]>([]);
  const [variablesLoading, setVariablesLoading] = useState(false);
  const [openSearchMunicipalityBottomSheet, setOpenSearchMunicipalityBottomSheet] = useState(false);
  const [openSearchModal, setOpenSearchModal] = useState(false);
  const [neighboringGeometries, setNeighboringGeometries] = useState<GeometryItem[]>([]);
  const [selectedNeighborData, setSelectedNeighborData] = useState<GeometryData | null>(null);
  const [neighborCardPosition, setNeighborCardPosition] = useState<{ x: number; y: number } | null>(
    null
  );
  const [loadingNeighborData, setLoadingNeighborData] = useState(false);
  const viewportDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const navigate = useNavigate();
  const { getPolygonsInBbox } = usePolygonDatabase();

  const { fullResponse, isStreaming, error: streamError } = useChatStream({
    input: streamInput,
    onComplete: (response, convId) => {
      if (!sessionId) {
        setSessionId(convId);
      }

      const assistantMessage: Message = {
        role: 'assistant',
        content: response,
        timestamp: getCurrentTimestamp(),
      };

      setMessages((prev) => [...prev, assistantMessage]);
      setStreamInput(null);
    },
    onError: (error) => {
      setStreamInput(null);

      if (
        error.includes('Foreign key constraint') ||
        error.includes('conversation_id') ||
        error.includes('Invalid conversation')
      ) {
        setSessionId(undefined);
      }
    },
  });

  const getCurrentTimestamp = () =>
    new Date().toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    });

  const handleSendMessage = (customMessage?: string) => {
    const messageToSend = customMessage || input.trim();
    if (!messageToSend || isStreaming) return;

    const userMessage: Message = {
      role: 'user',
      content: messageToSend,
      timestamp: getCurrentTimestamp(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');

    const chatInput: ChatStreamInput = {
      message: messageToSend,
      municipality_code: cdMun || '',
    };

    if (sessionId && sessionId !== 'blocked') {
      chatInput.conversation_id = sessionId;
    }

    setStreamInput(chatInput);
  };

  const handleInputChange = (value: string) => {
    setInput(value);
  };

  const handleSuggestionClick = (suggestion: string) => {
    handleSendMessage(suggestion.trim());
  };

  const municipality = municipalityData?.municipality;

  const { data: chatHistory, loading: chatHistoryLoading } = useQuery<{
    getConversations: { conversations: ChatConversation[]; total: number };
  }>(QUERY_CONVERSATIONS_HISTORY_BY_ID, {
    variables: {
      input: {
        municipality_code: cdMun,
      },
    },
  });

  const onlyCoordinates = (municipality?.geometry && municipality.geometry.coordinates) || [];

  const center = [-51.9253, -14.235] as [number, number];

  const handleViewportChange = useCallback(
    (bounds: ViewportBounds) => {
      if (viewportDebounceRef.current) {
        clearTimeout(viewportDebounceRef.current);
      }
      viewportDebounceRef.current = setTimeout(async () => {
        viewportDebounceRef.current = null;
        const polygons = await getPolygonsInBbox(bounds);
        const excluded = polygons.filter((p) => p.municipality_code !== cdMun);
        const items: GeometryItem[] = excluded.flatMap((p) => {
          const coordinates = p.geometry?.coordinates;
          if (!coordinates) {
            return [];
          }
          return [
            {
              coordinates,
              municipality_code: p.municipality_code,
            },
          ];
        });
        setNeighboringGeometries(items);
      }, 250);
    },
    [cdMun, getPolygonsInBbox]
  );

  const createPlaceholderGeometryData = useCallback(
    (municipalityCode: string): GeometryData => ({
      municipality_code: municipalityCode,
      code_uf: '',
      name_municipality: '',
      name_uf: '',
      biome: '',
      center_point: [],
      name_region: '',
      population: 0,
      classification_type: Classification.RURAL,
      dimensions_scores: [],
      municipality_score: {
        general_score_risk: '0',
        general_score_opportunity: '0',
        intensity_color_risk: '',
        intensity_color_opportunity: '',
        classification_risk: ClassificationOpportunityAndRisk.LOW,
        classification_opportunity: ClassificationOpportunityAndRisk.LOW,
      },
    }),
    []
  );

  const handleNeighborClick = useCallback(
    async (municipalityCode: string, position: { x: number; y: number }) => {
      setNeighborCardPosition(position);
      setSelectedNeighborData(createPlaceholderGeometryData(municipalityCode));
      setLoadingNeighborData(true);
      try {
        const { data } = await apolloClient.query<{
          municipalityScoresDetailed: MunicipalityScoresDetailedResponse;
        }>({
          query: QUERY_MUNICIPALITY_SCORES_DETAILED,
          variables: {
            municipality_code: municipalityCode,
            type: 'risk',
            bigquery_id: 'overall',
            language: currentLanguage,
          },
          fetchPolicy: 'network-only',
        });
        const scoreData = data.municipalityScoresDetailed;
        setSelectedNeighborData({
          municipality_code: scoreData.municipality_code,
          code_uf: scoreData.code_uf,
          name_municipality: scoreData.name_municipality,
          name_uf: scoreData.name_uf,
          sigla_uf: scoreData.sigla_uf,
          biome: scoreData.biome,
          center_point: [],
          name_region: scoreData.name_region,
          population: scoreData.population,
          classification_type: scoreData.classification_type,
          dimensions_scores: [],
          municipality_score: {
            general_score_risk: scoreData.score,
            general_score_opportunity: scoreData.score,
            intensity_color_risk: '',
            intensity_color_opportunity: '',
            classification_risk: scoreData.classification as ClassificationOpportunityAndRisk,
            classification_opportunity: scoreData.classification as ClassificationOpportunityAndRisk,
          },
        });
      } finally {
        setLoadingNeighborData(false);
      }
    },
    [apolloClient, currentLanguage, createPlaceholderGeometryData]
  );

  const handleCloseNeighborCard = useCallback(() => {
    setSelectedNeighborData(null);
    setNeighborCardPosition(null);
  }, []);

  const handleNeighborCardClick = useCallback(() => {
    if (selectedNeighborData) {
      navigate(`${AppRouters.municipality}/${selectedNeighborData.municipality_code}`);
      setSelectedNeighborData(null);
      setNeighborCardPosition(null);
    }
  }, [navigate, selectedNeighborData]);

  useEffect(() => {
    setNeighboringGeometries([]);
  }, [cdMun]);

  useEffect(() => {
    return () => {
      if (viewportDebounceRef.current) {
        clearTimeout(viewportDebounceRef.current);
      }
    };
  }, []);

  const kpiLoading = new Array(5).fill(null);

  useEffect(() => {
    if (chatHistory?.getConversations?.total && chatHistory.getConversations.total > 0) {
      const chatMessages = chatHistory.getConversations.conversations[0].messages;
      const transformedMessages: Message[] = chatMessages.map((msg) => ({
        role: msg.role as 'user' | 'assistant',
        content: msg.content,
        timestamp: getCurrentTimestamp(),
      }));
      setMessages(transformedMessages);
      setSessionId(chatMessages[chatMessages.length - 1]?.conversationId);
    } else {
      setMessages([]);
      setSessionId(undefined);
    }
  }, [chatHistory]);

  useEffect(() => {
    if (insightText && insightText.trim()) {
      setIsChatOpen(true);

      setInput(insightText);

      const timer = setTimeout(() => {
        handleSendMessage(insightText.trim());
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [insightText]);

  useEffect(() => {
    const fetchVariables = async () => {
      setVariablesLoading(true);
      try {
        const { data } = await apolloClient.query<{
          variablesByType: Variable[];
        }>({
          query: QUERY_VARIABLES_BY_TYPE,
          variables: {
            classificationType: 'caracteristica_municipio',
            language: currentLanguage,
          },
        });
        setVariables(data.variablesByType);
      } catch {
        // Error fetching variables
      } finally {
        setVariablesLoading(false);
      }
    };
    fetchVariables();
  }, [currentLanguage]);

  const isLoading = municipalityLoading || variablesLoading;

  const ScoreCard = () => {
    return (
      <>
        {isLoading ? (
          <Flex fullWidth>
            <Skeleton variant="text" width="100%" height="471px" sx={{ transform: 'none' }} />
          </Flex>
        ) : (
          <DimensionsNote data={municipalityData as MunicipalityWithDimensions} />
        )}
      </>
    );
  };

  return (
    <>
      <StyledFab
        onClick={() => {
          onResetInsightText?.();
          setIsChatOpen(!isChatOpen);
        }}
      >
        <SparklesIcon sx={{ color: 'white', width: '48px', height: '48px' }} />
      </StyledFab>

      {isChatOpen && (
        <Chat
          onMinimize={() => {
            onResetInsightText?.();
            setIsChatOpen(false);
          }}
          code_municipality={cdMun}
          municipalityName={municipalityData?.municipality.name_municipality}
          messages={messages}
          loading={isStreaming || chatHistoryLoading}
          input={input}
          onInputChange={handleInputChange}
          onSendMessage={handleSendMessage}
          onSuggestionClick={handleSuggestionClick}
          streamingResponse={isStreaming ? fullResponse : undefined}
          error={streamError}
        />
      )}

      <Container>
        <StyledPaper variant="outlined">
          <TitleHeader>
            {isLoading ? (
              <Skeleton variant="text" width="100%" height="40px" sx={{ transform: 'none' }} />
            ) : (
              <>
                {municipalityData?.municipality && (
                  <>
                    <Text variant={isMobile ? 'h6' : 'h5'} color="text.primary">
                      {municipalityData?.municipality.name_municipality}
                    </Text>

                    <Desktop>
                      <StyledChangeMunicipalityButton
                        variant="outlined"
                        onClick={() => setOpenSearchModal(true)}
                      >
                        <EditOutlined color="action" />
                        {t('button.button.change_municipality')}
                      </StyledChangeMunicipalityButton>
                    </Desktop>

                    <Mobile>
                      <IconButton
                        sx={{ padding: theme.spacing(0.5, 0, 0) }}
                        onClick={() => setOpenSearchMunicipalityBottomSheet(true)}
                      >
                        <EditOutlined color="action" />
                      </IconButton>
                    </Mobile>
                  </>
                )}
              </>
            )}
          </TitleHeader>

          {isLoading ? (
            <Flex gap={3} fullWidth>
              {kpiLoading.map((_, index) => (
                <Skeleton
                  key={index}
                  variant="text"
                  width="100%"
                  height="110px"
                  sx={{ transform: 'none' }}
                />
              ))}
            </Flex>
          ) : (
            <MunicipalityKpis municipality={municipality} variables={variables} />
          )}

          <StyledScoreCardMapContainer>
            <Desktop>
              <ScoreCard />
            </Desktop>

            {isLoading || !municipality ? (
              <Flex fullWidth>
                <Skeleton variant="text" width="100%" height="471px" sx={{ transform: 'none' }} />
              </Flex>
            ) : (
              <MapContainer>
                <StaticMap
                  geometryCoordinates={onlyCoordinates}
                  center={center}
                  mapColor={{
                    fillColor: theme.palette['blue-ribbon'][900],
                    lineColor: '#646464',
                  }}
                  onViewportChange={handleViewportChange}
                  neighboringGeometries={neighboringGeometries}
                  onNeighborClick={handleNeighborClick}
                />
              </MapContainer>
            )}

            <Mobile>
              <ScoreCard />
            </Mobile>
          </StyledScoreCardMapContainer>
        </StyledPaper>

        <Flex direction="column" gap={1.5} marginTop={3} paddingBottom={isMobile ? 0 : 3} fullWidth>
          <Text variant="h5" color="text.primary">
            <SafeMarkdown text={t('dimension.dimension.title')} />
          </Text>

          {tabs}
        </Flex>
      </Container>

      <Desktop>
        {openSearchModal && (
          <>
            <Dialog open={openSearchModal} onClose={() => setOpenSearchModal(false)}>
              <DialogTitle
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Text variant="h6">
                  <SafeMarkdown text={t('modal.modal.municipality.search.title')} />
                </Text>
                <IconButton onClick={() => setOpenSearchModal(false)}>
                  <Close color="action" />
                </IconButton>
              </DialogTitle>
              <DialogContent
                sx={{
                  width: '100%',
                  minWidth: '600px',
                  height: 'fit-content',
                  padding: theme.spacing(3),
                }}
              >
                <MunicipalityAutocomplete
                  name="search-municipality"
                  placeholder={t('placeholder.placeholder.search.municipality')}
                  onChange={(_, value) => {
                    if (value) {
                      onMunicipalityChange?.(value.municipality_code);
                      setOpenSearchModal(false);
                    } else {
                      onMunicipalityChange?.('');
                    }
                  }}
                  margin="none"
                  fullWidth
                />
              </DialogContent>
            </Dialog>
          </>
        )}
      </Desktop>

      <Mobile>
        <SearchMunicipalityBottomSheet
          open={openSearchMunicipalityBottomSheet}
          onClose={() => setOpenSearchMunicipalityBottomSheet(false)}
          variant="municipality"
          onMunicipalityChange={(municipality) =>
            onMunicipalityChange?.(municipality?.municipality_code || '')
          }
        />
      </Mobile>

      {selectedNeighborData && neighborCardPosition && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1300,
            pointerEvents: 'auto',
          }}
        >
          <MapCardBackdrop onClick={handleCloseNeighborCard} />
          <div
            className="map-card"
            style={{
              left: `${neighborCardPosition.x}px`,
              top: `${neighborCardPosition.y}px`,
              zIndex: 1310,
              pointerEvents: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <MapCard
              geometryData={selectedNeighborData}
              hasDimensionsScores={false}
              variant="risk"
              loadingData={loadingNeighborData}
              href={`${AppRouters.municipality}/${selectedNeighborData.municipality_code}`}
              onClose={handleCloseNeighborCard}
              onClick={handleNeighborCardClick}
            />
          </div>
        </Box>
      )}
    </>
  );
};
